import React from 'react';
import { Loader2, DollarSign, CreditCard, History, FileText, Download } from 'lucide-react';

export function DriverPayoutsTab({
  earnings, loadingEarnings, driver, user,
  payoutForm, setPayoutForm, payoutSaving, paypalConfirm, setPaypalConfirm,
  handlePayout, checkingPayoutStatus, handleCheckPayoutStatus,
  handleExportCSV, handleExportPDF
}) {
  return (
    <div data-testid="tab-content-payouts">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-slate-800">Auszahlungen</h2>
        <div className="flex gap-2">
          <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors" data-testid="export-pdf-payouts-btn">
            <FileText size={14} /> PDF Export
          </button>
          <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-4 py-2 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors" data-testid="export-csv-payouts-btn">
            <Download size={14} /> CSV Export
          </button>
        </div>
      </div>
      {loadingEarnings ? (
        <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-orange-500" /></div>
      ) : earnings ? (
        <div className="space-y-6">
          <div className={`rounded-xl p-6 border ${earnings.balance_owed > 0 ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium ${earnings.balance_owed > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>Offener Betrag an Fahrer</p>
                <p className={`text-3xl font-bold mt-1 ${earnings.balance_owed > 0 ? 'text-orange-800' : 'text-emerald-800'}`} data-testid="payout-balance">{earnings.balance_owed} EUR</p>
                <p className="text-xs text-slate-500 mt-1">Verdient: {earnings.total_earned} EUR | Ausgezahlt: {earnings.total_paid} EUR</p>
              </div>
              {earnings.balance_owed > 0 && (
                <div className="text-right">
                  <p className="text-xs text-orange-600 font-medium">Wöchentliche Abrechnung</p>
                  <p className="text-xs text-slate-500">Kunde bezahlt 366transfer.com</p>
                  <p className="text-xs text-slate-500">Wir bezahlen den Fahrer</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-4">Neue Auszahlung erstellen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="text-xs text-slate-500 block mb-1.5">Betrag (EUR)</label>
                <input type="number" step="0.01" value={payoutForm.amount}
                  onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  placeholder={earnings.balance_owed > 0 ? String(earnings.balance_owed) : '0.00'}
                  data-testid="payout-amount-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1.5">Zahlungsmethode</label>
                <select value={payoutForm.method} onChange={e => setPayoutForm(f => ({ ...f, method: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="payout-method-select">
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Banküberweisung</option>
                  <option value="instapay">InstaPay</option>
                  <option value="vodafone_cash">Vodafone Cash</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1.5">Notiz (optional)</label>
                <input type="text" value={payoutForm.notes} onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  placeholder="z.B. KW 08" data-testid="payout-notes-input" />
              </div>
              <div className="flex items-end">
                <button onClick={() => handlePayout(false)} disabled={payoutSaving || !payoutForm.amount || Number(payoutForm.amount) <= 0}
                  className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
                  data-testid="payout-manual-btn">
                  {payoutSaving ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />} Manuell markieren
                </button>
              </div>
            </div>

            {payoutForm.method === 'paypal' && driver.paypal_email && (
              <div className="mt-4">
                {!paypalConfirm ? (
                  <button onClick={() => { if (payoutForm.amount && Number(payoutForm.amount) > 0) setPaypalConfirm(true); }}
                    disabled={payoutSaving || !payoutForm.amount || Number(payoutForm.amount) <= 0}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    data-testid="paypal-auto-btn">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.603c-.564 0-1.04.408-1.13.964L7.076 21.337z"/></svg>
                    PayPal Auszahlung senden ({payoutForm.amount || '0'} EUR an {driver.paypal_email})
                  </button>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5" data-testid="paypal-confirm-dialog">
                    <p className="text-sm font-bold text-blue-900 mb-2">PayPal-Auszahlung bestätigen</p>
                    <div className="text-sm text-blue-800 space-y-1 mb-4">
                      <p>Betrag: <span className="font-bold">{payoutForm.amount} EUR</span></p>
                      <p>An: <span className="font-bold">{driver.paypal_email}</span></p>
                      <p>Fahrer: <span className="font-bold">{user.name}</span></p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handlePayout(true)} disabled={payoutSaving}
                        className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="paypal-confirm-send-btn">
                        {payoutSaving ? <><Loader2 size={14} className="animate-spin" /> Wird gesendet...</> : 'Jetzt senden'}
                      </button>
                      <button onClick={() => setPaypalConfirm(false)} className="px-4 py-2.5 bg-white text-slate-600 rounded-lg text-sm font-medium border border-slate-200 hover:bg-slate-50"
                        data-testid="paypal-confirm-cancel-btn">Abbrechen</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {payoutForm.method === 'paypal' && !driver.paypal_email && (
              <p className="mt-3 text-xs text-amber-600 bg-amber-50 px-4 py-2.5 rounded-lg">Dieser Fahrer hat keine PayPal-E-Mail hinterlegt. Bitte zuerst unter "Persönliche Daten" ergänzen.</p>
            )}
          </div>

          {earnings.payouts?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-3">Auszahlungsverlauf</h3>
              <div className="space-y-2">
                {earnings.payouts.map(p => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid={`payout-row-${p.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {p.amount} EUR via {p.method === 'bank_transfer' ? 'Banküberweisung' : p.method === 'vodafone_cash' ? 'Vodafone Cash' : p.method === 'paypal' ? 'PayPal' : p.method}
                        {p.paypal_email && <span className="text-xs text-blue-500 ml-1.5">({p.paypal_email})</span>}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.notes || '-'} | von {p.paid_by_name}</p>
                      {p.paypal_batch_id && <p className="text-xs text-slate-400 mt-0.5">Batch: {p.paypal_batch_id}</p>}
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('de-DE') : '-'}</p>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                          p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          p.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                          p.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {p.status === 'paid' ? 'Bezahlt' : p.status === 'processing' ? 'Verarbeitung...' : p.status === 'failed' ? 'Fehlgeschlagen' : p.status}
                        </span>
                      </div>
                      {p.paypal_batch_id && p.status === 'processing' && (
                        <button onClick={() => handleCheckPayoutStatus(p.id)} disabled={checkingPayoutStatus === p.id}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Status aktualisieren"
                          data-testid={`payout-refresh-${p.id}`}>
                          {checkingPayoutStatus === p.id ? <Loader2 size={14} className="animate-spin" /> : <History size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">Daten werden geladen...</div>
      )}
    </div>
  );
}
